/**
 * Candidate — detail (the person).  GET /candidates/:id
 * Profile + every application across openings (the single history view) + resume upload.
 * CTC honors rule R6 (absent key → 🔒 Restricted).
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, MapPin, Clock, Briefcase, FileText, MessageSquare } from 'lucide-react';
import { Button, Skeleton } from '../../components/ui';
import { Card } from '../../components/ui/Card';
import { useCandidate } from '../../features/recruitment/hooks/useCandidates';
import {
	Avatar,
	CtcValue,
	ApplicationsTable,
	ResumeUploadCard,
	EmptyState,
	ErrorState,
} from '../../features/recruitment';
import { formatExperience } from '../../features/recruitment/utils/format';

const Field: React.FC<{ icon?: React.ReactNode; label: string; value: React.ReactNode }> = ({ icon, label, value }) => (
	<div>
		<p className='text-[11px] uppercase tracking-wide text-gray-400'>{label}</p>
		<p className='mt-0.5 flex items-center gap-1.5 text-sm font-medium text-gray-800'>
			{icon && <span className='text-gray-400'>{icon}</span>}
			{value}
		</p>
	</div>
);

export const CandidateDetail: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const candidateId = id ? Number(id) : undefined;

	const { data, isLoading, isError, refetch } = useCandidate(candidateId);
	const candidate = data?.candidate;
	const applications = data?.applications ?? [];

	if (isLoading) {
		return (
			<div className='min-h-screen bg-gray-50 p-6'>
				<div className='mx-auto max-w-5xl space-y-4'>
					<Skeleton className='h-8 w-64' />
					<Skeleton className='h-48 w-full' />
					<Skeleton className='h-64 w-full' />
				</div>
			</div>
		);
	}

	if (isError || !candidate) {
		return (
			<div className='min-h-screen bg-gray-50 p-6'>
				<div className='mx-auto max-w-5xl'>
					<ErrorState message='We could not load this candidate.' onRetry={() => refetch()} />
				</div>
			</div>
		);
	}

	const roleLine = [candidate.current_role, candidate.current_company].filter(Boolean).join(' · ');

	return (
		<div className='min-h-screen bg-gray-50 p-6'>
			<div className='mx-auto max-w-5xl'>
				<button
					type='button'
					onClick={() => navigate(-1)}
					className='mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800'
				>
					<ArrowLeft className='h-4 w-4' /> Back
				</button>

				{/* Profile */}
				<Card className='mb-6'>
					<div className='flex items-start gap-4'>
						<Avatar name={candidate.full_name} className='h-12 w-12 text-base' />
						<div className='min-w-0 flex-1'>
							<h1 className='text-2xl font-semibold text-gray-900'>{candidate.full_name}</h1>
							{roleLine && (
								<p className='mt-0.5 flex items-center gap-1 text-sm text-gray-500'>
									<Briefcase className='h-3.5 w-3.5' /> {roleLine}
								</p>
							)}
						</div>
						<Button variant='primary' onClick={() => navigate('/recruitment/candidates/add')}>
							Add to another opening
						</Button>
					</div>

					<div className='mt-5 grid grid-cols-2 gap-4 border-t border-gray-100 pt-5 md:grid-cols-3 lg:grid-cols-4'>
						{candidate.phone_raw && <Field icon={<Phone className='h-3.5 w-3.5' />} label='Phone' value={candidate.phone_raw} />}
						{candidate.email_raw && <Field icon={<Mail className='h-3.5 w-3.5' />} label='Email' value={candidate.email_raw} />}
						{candidate.location && <Field icon={<MapPin className='h-3.5 w-3.5' />} label='Location' value={candidate.location} />}
						<Field label='Total experience' value={formatExperience(candidate.total_experience)} />
						{candidate.notice_period_days != null && (
							<Field icon={<Clock className='h-3.5 w-3.5' />} label='Notice period' value={`${candidate.notice_period_days} days`} />
						)}
						<Field label='Current CTC' value={<CtcValue candidate={candidate} field='current_ctc' />} />
						<Field label='Expected CTC' value={<CtcValue candidate={candidate} field='expected_ctc' />} />
					</div>
				</Card>

				{/* All applications */}
				<Card className='mb-6'>
					<div className='mb-4 flex items-center justify-between'>
						<h2 className='text-base font-semibold text-gray-900'>Applications</h2>
						<span className='text-sm text-gray-500'>{applications.length} total</span>
					</div>
					{applications.length === 0 ? (
						<EmptyState title='No applications yet' message='This person has not been applied to any opening.' />
					) : (
						<ApplicationsTable rows={applications} onOpen={appId => navigate(`/recruitment/applications/${appId}`)} />
					)}
				</Card>

				{/* Resume */}
				<Card className='mb-6'>
					<div className='mb-3 flex items-center gap-2'>
						<FileText className='h-4 w-4 text-gray-400' />
						<h2 className='text-base font-semibold text-gray-900'>Resume</h2>
					</div>
					<p className='mb-3 text-sm text-gray-500'>
						Upload a resume for this candidate. Uploaded DOCX/TXT resumes become searchable in Resume Search
						(PDF text indexing arrives in a later phase).
					</p>
					<ResumeUploadCard candidateId={candidate.id} />
				</Card>

				{/* Phase 3/4 stub — left intentionally for later */}
				<Card className='border-dashed'>
					<div className='flex items-center gap-2 text-gray-400'>
						<MessageSquare className='h-4 w-4' />
						<span className='text-sm'>Communication &amp; offers will appear here in a later phase.</span>
					</div>
				</Card>
			</div>
		</div>
	);
};
